import { useApolloClient, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { ReloadOutlined } from '@ant-design/icons';
import { Button, message, Result, Tabs } from 'antd';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { NoPermission } from '../../../components/NoPermission';
import TablePaginationBar from '../../../components/TablePaginationBar';
import { ResourceCode } from '../../../constants/resourceCodes';
import { useWarehouseContext } from '../../../contexts/WarehouseContext';
import { WarehouseBusinessType } from '../../../graphql/types/dashboard/SortingDashboardTypes';
import { SiteType } from '../../../graphql/types/SiteType';
import { hasResource } from '../../../utils/resourceAuth';
import BagDetailDrawer from '../bags/BagDetailDrawer';
import { BatchDetailDrawer } from './components/BatchDetailDrawer';
import { BatchFilterBar } from './components/BatchFilterBar';
import { BatchTable } from './components/BatchTable';
import { BatchToolbar } from './components/BatchToolbar';
import { CreateBagsDrawer } from './components/CreateBagsDrawer';
import { EndSortingModal } from './components/EndSortingModal';
import { LoadDetailDrawer } from './components/LoadDetailDrawer';
import { MoveInDrawer } from './components/MoveInDrawer';
import { MoveOutDrawer } from './components/MoveOutDrawer';
import { NewBatchDrawer } from './components/NewBatchDrawer';
import { CreateBagsTarget, EndSortingTarget } from './components/overlayProps';
import { PrintQuantityModal } from './components/PrintQuantityModal';
import { DEFAULT_PAGE_SIZE, ENABLE_LOAD_DETAIL, PAGE_SIZE_OPTIONS } from './constants';
import {
    CANCEL_SORTING_BATCHES, CancelSortingBatchesResponse, CancelSortingBatchesVariables,
    GET_SORTING_BATCH_BAGS_FOR_PRINT, GET_SORTING_BATCH_DESTINATIONS, GET_SORTING_BATCHES, GET_SORTING_BATCHES_BY_DESTINATION,
    GetSortingBatchBagsForPrintResponse, GetSortingBatchBagsForPrintVariables,
    GetSortingBatchDestinationsResponse, GetSortingBatchDestinationsVariables,
    GetSortingBatchesByDestinationResponse, GetSortingBatchesByDestinationVariables,
    GetSortingBatchesResponse, GetSortingBatchesVariables,
} from './graphql/sortingBatch';
import { GET_SORTING_BATCH_BAGS, GET_SORTING_BATCH_OPERATION_LOGS } from './graphql/sortingBatchDetail';
import { usePrintSelectedTags } from './hooks/usePrintSelectedTags';
import { toPrintJobs } from './mock/batchMockActions';
import { listBatchBags, MoveSource } from './mock/batchMockDerive';
import { SortingBatchProvider, useSortingBatchStore } from './state/SortingBatchContext';
import './sortingBatch.css';
import { BatchBag, BatchDirection, BatchRowView, BatchStage, PrintJob } from './types';
import { DEFAULT_BATCH_FILTERS, BatchFilters, filterBatches, filterOptionsOf } from './utils/batchFilterUtils';
import { resolveHubPrefix } from './utils/batchNameUtils';
import { blockedReasonOf, checkCancel } from './utils/batchRules';
import { formatBatchTime } from './utils/formatUtils';
import { toRealBatchRowView } from './utils/realBatchAdapter';

/** 详情抽屉之上的单个抽屉槽位（与原型一致：同一时刻只开一个） */
type ActiveDrawer =
    | { type: 'load'; batchId: number; stage: BatchStage | null }
    | { type: 'bag'; bagNumber: string }
    | { type: 'newBatch' }
    | { type: 'createBags'; batches: CreateBagsTarget[] }
    | { type: 'moveIn'; source: MoveSource | null }
    | { type: 'moveOut' };

const SortingBatchPageBody: React.FC = () => {
    const { t } = useTranslation();
    const { state, dispatch } = useSortingBatchStore();
    const { currentWarehouse } = useWarehouseContext();
    const warehouseId = currentWarehouse?.id;
    const [direction, setDirection] = useState<BatchDirection>('OUTBOUND');
    const [draftFilters, setDraftFilters] = useState<BatchFilters>(DEFAULT_BATCH_FILTERS);
    const [filters, setFilters] = useState<BatchFilters>(DEFAULT_BATCH_FILTERS);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [detailId, setDetailId] = useState<number | null>(null);
    const [drawer, setDrawer] = useState<ActiveDrawer | null>(null);
    const [endSortingTargets, setEndSortingTargets] = useState<EndSortingTarget[] | null>(null);
    const [printJobs, setPrintJobs] = useState<PrintJob[] | null>(null);
    const { sendToPrinter } = usePrintSelectedTags();
    const outbound = direction === 'OUTBOUND';

    const { data: outboundData, refetch: refetchOutbound } = useQuery<GetSortingBatchesResponse, GetSortingBatchesVariables>(
        GET_SORTING_BATCHES,
        { variables: { warehouseId: warehouseId || '0' }, skip: !outbound || !warehouseId, fetchPolicy: 'network-only' },
    );
    const { data: inboundData, refetch: refetchInbound } = useQuery<GetSortingBatchesByDestinationResponse, GetSortingBatchesByDestinationVariables>(
        GET_SORTING_BATCHES_BY_DESTINATION,
        { variables: { warehouseId: warehouseId || '0' }, skip: outbound || !warehouseId, fetchPolicy: 'network-only' },
    );
    const rows = useMemo(() => {
        if (outbound) {
            return (outboundData?.sortingBatches || []).map((b) => toRealBatchRowView(b, state.refreshedAt));
        }
        return (inboundData?.sortingBatchesByDestination || []).map((b) => toRealBatchRowView(b, state.refreshedAt));
    }, [outbound, outboundData, inboundData, state.refreshedAt]);
    // 目的地筛选项：发出视角复用新建批次已在用的目的地树查询，不依赖"是否已有批次"——
    // 从已查到的批次里派生会漏掉还没建过批次的目的地
    const { data: destinationsData } = useQuery<GetSortingBatchDestinationsResponse, GetSortingBatchDestinationsVariables>(
        GET_SORTING_BATCH_DESTINATIONS,
        { variables: { warehouseId: warehouseId || '0' }, skip: !outbound || !warehouseId, fetchPolicy: 'network-only' },
    );
    // todo batch mock: 接收视角筛选项待后端提供接收视角的 Query 后一并改造
    const filterOptions = useMemo(() => {
        const base = filterOptionsOf(rows);
        if (!outbound) return base;
        // warehouseCode 本身已是完整 Hub-Whs 两段码（如 "LAX-DC"），不能再拼一次 Hub（否则 LAX-LAX-DC）；
        // 与批次行 destination 口径一致（realBatchAdapter：destinationWhsCode || destinationHubCode）
        const destinations = Array.from(new Set(
            (destinationsData?.sortingBatchDestinations || [])
                .map((d) => d.warehouseCode || d.hubCode),
        )).sort();
        return { ...base, destinations };
    }, [rows, outbound, destinationsData]);
    const filtered = useMemo(() => filterBatches(rows, filters), [rows, filters]);
    // 写操作让筛选结果变少时（如取消 / 结束分拣后不再满足状态筛选），页码回收到最后一页
    const currentPage = Math.min(page, Math.max(1, Math.ceil(filtered.length / pageSize)));
    const pageRows = useMemo(
        () => filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize),
        [filtered, currentPage, pageSize],
    );
    const selected = useMemo(() => filtered.filter((b) => selectedIds.includes(b.id)), [filtered, selectedIds]);
    const detailBatch = useMemo(() => rows.find((b) => b.id === detailId) ?? null, [rows, detailId]);

    const [cancelSortingBatches] = useMutation<CancelSortingBatchesResponse, CancelSortingBatchesVariables>(CANCEL_SORTING_BATCHES);

    const switchDirection = (key: string) => {
        setDirection(key as BatchDirection);
        setSelectedIds([]);
        setDetailId(null);
        setDrawer(null);
        // 发出 / 接收的筛选项（创建地、目的地）不同，切换时不沿用
        setDraftFilters(DEFAULT_BATCH_FILTERS);
        setFilters(DEFAULT_BATCH_FILTERS);
        setPage(1);
    };

    // 筛选 / 重置 / 翻页 / 改每页条数时清空勾选，勾选只在当前页内有效，避免对看不到的批次操作
    // （同 wms/account/sorting-labor-team/SortingLaborTeamPage）
    const handleFilter = () => {
        setFilters(draftFilters);
        setPage(1);
        setSelectedIds([]);
    };

    const handleReset = () => {
        setDraftFilters(DEFAULT_BATCH_FILTERS);
        setFilters(DEFAULT_BATCH_FILTERS);
        setPage(1);
        setSelectedIds([]);
    };

    const handleRefresh = async () => {
        if (outbound) {
            await refetchOutbound();
        } else {
            await refetchInbound();
        }
        dispatch({ type: 'REFRESH', time: Date.now() });
        message.success(t('wms.sortingBatch.messages.refreshed'));
    };

    const requireSelection = (): boolean => {
        if (selected.length) return true;
        message.warning(t('wms.sortingBatch.messages.selectBatchFirst'));
        return false;
    };

    const warnBlocked = useCallback((row: BatchRowView): boolean => {
        const reason = blockedReasonOf(row.status);
        if (!reason) return false;
        message.warning(t(reason === 'sortingEnded'
            ? 'wms.sortingBatch.messages.sortingEndedNoNewBags'
            : 'wms.sortingBatch.messages.batchTerminated'));
        return true;
    }, [t]);

    const [fetchBagsForPrint] = useLazyQuery<GetSortingBatchBagsForPrintResponse, GetSortingBatchBagsForPrintVariables>(
        GET_SORTING_BATCH_BAGS_FOR_PRINT,
        { fetchPolicy: 'network-only' },
    );

    const jobsOf = useCallback(async (list: BatchRowView[]): Promise<PrintJob[]> => {
        if (!outbound) {
            // todo batch mock: 接收视角批量打印待后端提供接收视角的包牌查询后一并改造
            return list.flatMap((b) => toPrintJobs(listBatchBags(state, b), b));
        }
        if (!warehouseId) return [];
        const { data } = await fetchBagsForPrint({ variables: { warehouseId, batchIds: list.map((b) => String(b.id)) } });
        const rowById = new Map(list.map((b) => [String(b.id), b]));
        return (data?.sortingBatchBagsForPrint || []).flatMap((entry) => {
            const row = rowById.get(entry.batchId);
            if (!row) return [];
            return entry.bags.map((bag) => ({
                bagId: bag.bagId,
                bagNumber: bag.bagNumber,
                sortingCode: bag.sortingCode || '',
                status: bag.status,
                batchNumber: row.batchNumber,
                // 批次打印：带上正在打印的那个批次 id，后端据此取标签批次段（而非物理当前批次）
                batchId: entry.batchId,
                destination: row.destination,
                copies: 1,
            }));
        });
    }, [outbound, state, warehouseId, fetchBagsForPrint]);

    const handleToolbarPrint = async () => {
        if (!requireSelection()) return;
        const jobs = await jobsOf(selected);
        if (!jobs.length) {
            message.warning(t('wms.sortingBatch.messages.selectedHaveNoBags'));
            return;
        }
        setPrintJobs(jobs);
    };

    const handleCancelBatches = async () => {
        if (!requireSelection()) return;
        // 前端这道预检查对真实批次是弱校验（包牌/包裹数暂无真实数据回填，恒为0），真正的拦截以后端为准
        const { allowed, rejected } = checkCancel(selected, (b) => listBatchBags(state, b).length);
        if (!allowed) {
            message.warning(t('wms.sortingBatch.messages.cannotCancel', { names: rejected.map((b) => b.batchNumber).join(String(t('wms.sortingBatch.common.separator'))) }));
            return;
        }
        try {
            await cancelSortingBatches({ variables: { input: { batchIds: selected.map((b) => String(b.id)) } } });
            message.success(t('wms.sortingBatch.messages.cancelled', { count: selected.length }));
            setSelectedIds([]);
            if (outbound) await refetchOutbound();
        } catch (error: any) {
            const messageKey = error?.graphQLErrors?.[0]?.extensions?.messageKey;
            message.error(messageKey ? (t(messageKey) as string) || messageKey : t('wms.sortingBatch.messages.cancelFailed'));
        }
    };

    const handleToolbarEndSorting = () => {
        if (!requireSelection()) return;
        setEndSortingTargets(selected.map((b) => ({ id: b.id, batchNumber: b.batchNumber, status: b.status })));
    };

    const handleRowCreateBags = useCallback((row: BatchRowView) => {
        if (warnBlocked(row)) return;
        setDrawer({ type: 'createBags', batches: [{ id: row.id, batchNumber: row.batchNumber, destination: row.destination, status: row.status }] });
    }, [warnBlocked]);

    const handleRowPrint = useCallback(async (row: BatchRowView) => {
        const jobs = await jobsOf([row]);
        if (!jobs.length) {
            message.warning(t('wms.sortingBatch.messages.batchHasNoBags'));
            return;
        }
        setPrintJobs(jobs);
    }, [jobsOf, t]);

    const handleRowEndSorting = useCallback((row: BatchRowView) => {
        if (warnBlocked(row)) return;
        setEndSortingTargets([{ id: row.id, batchNumber: row.batchNumber, status: row.status }]);
    }, [warnBlocked]);

    const openDetail = useCallback((row: BatchRowView) => setDetailId(row.id), []);
    // 包牌详情复用 WMS→包牌 的抽屉（含批次记录），按包牌号打开
    const openBagDetail = useCallback((bag: BatchBag) => setDrawer({ type: 'bag', bagNumber: bag.bagNumber }), []);
    const openMoveBags = useCallback((source: MoveSource) => setDrawer({ type: 'moveIn', source }), []);
    const closeDrawer = useCallback(() => setDrawer(null), []);
    const openPrintFromDrawer = useCallback((jobs: PrintJob[]) => {
        setDrawer(null);
        setPrintJobs(jobs);
    }, []);
    const apolloClient = useApolloClient();
    // 从详情发起的创建包牌、移动到其他批次、结束分拣会改包牌与操作历史，浮层结束后重查；详情没开时没有这两个查询，不查
    // 建包 / 移入 / 结束分拣后：批次列表（包牌数·包裹数·状态）要刷，当前详情抽屉（若打开）也要刷。
    // 之前只刷详情、且 detailId=null（从列表行直接操作）时什么都不刷，导致列表计数不更新。
    const refreshListAndDetail = useCallback(() => {
        if (outbound) refetchOutbound();
        if (detailId !== null) {
            apolloClient.refetchQueries({ include: [GET_SORTING_BATCH_BAGS, GET_SORTING_BATCH_OPERATION_LOGS] });
        }
    }, [outbound, refetchOutbound, apolloClient, detailId]);

    return (
        <div className="flex flex-col h-full bg-white rounded-lg shadow-sm px-6 pb-6">
            <Tabs
                activeKey={direction}
                onChange={switchDirection}
                items={[
                    { key: 'OUTBOUND', label: t('wms.sortingBatch.tabs.outbound') },
                    { key: 'INBOUND', label: t('wms.sortingBatch.tabs.inbound') },
                ]}
                tabBarExtraContent={(
                    <div className="flex items-center gap-3">
                        <span className="whitespace-nowrap text-[13px] text-[#86909c]">
                            {t('wms.sortingBatch.list.updatedAt', { time: formatBatchTime(state.refreshedAt) })}
                        </span>
                        <Button icon={<ReloadOutlined />} onClick={handleRefresh}>{t('wms.sortingBatch.list.refresh')}</Button>
                    </div>
                )}
            />

            <BatchFilterBar
                filters={draftFilters}
                onChange={setDraftFilters}
                onFilter={handleFilter}
                onReset={handleReset}
                creationSites={filterOptions.creationSites}
                destinations={filterOptions.destinations}
            />

            {outbound ? (
                <BatchToolbar
                    selectedCount={selected.length}
                    onNewBatch={() => setDrawer({ type: 'newBatch' })}
                    onPrintBags={handleToolbarPrint}
                    onMoveIn={() => setDrawer({ type: 'moveIn', source: null })}
                    onMoveOut={() => setDrawer({ type: 'moveOut' })}
                    onCancelBatches={handleCancelBatches}
                    onEndSorting={handleToolbarEndSorting}
                />
            ) : (
                <div className="mb-4 rounded border border-solid border-[#e9dcff] bg-[#faf7ff] px-3.5 py-2.5 text-[12.5px] text-[#4e5969]">
                    {t('wms.sortingBatch.list.inboundHint')}
                </div>
            )}

            <div className="flex-1 overflow-auto">
                <BatchTable
                    rows={pageRows}
                    direction={direction}
                    selectedIds={selectedIds}
                    onSelectChange={setSelectedIds}
                    onOpenDetail={openDetail}
                    onCreateBags={handleRowCreateBags}
                    onPrintBags={handleRowPrint}
                    onEndSorting={handleRowEndSorting}
                />
            </div>

            <TablePaginationBar
                total={filtered.length}
                current={currentPage}
                pageSize={pageSize}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                showSizeChanger
                onChange={(p, size) => {
                    setPage(p);
                    setPageSize(size);
                    setSelectedIds([]);
                }}
            />

            {/* 详情抽屉在下层，其余抽屉 / 弹窗叠加其上 */}
            <BatchDetailDrawer
                open={detailId !== null}
                batch={detailBatch}
                direction={direction}
                onClose={() => setDetailId(null)}
                onOpenBagDetail={openBagDetail}
                onOpenPrint={setPrintJobs}
                onOpenMoveBags={openMoveBags}
                onOpenCreateBags={(batches) => setDrawer({ type: 'createBags', batches })}
                onOpenEndSorting={setEndSortingTargets}
            />
            {/* 装载明细延期（2026-10-14，plan §4.10）：唯一入口「5 列阶段统计」已删除（改由数据组在 BI 看板实现），
                ENABLE_LOAD_DETAIL 为 false 时不挂载，代码保留；恢复时需另加打开入口（setDrawer({ type: 'load', ... })） */}
            {ENABLE_LOAD_DETAIL && (
                <LoadDetailDrawer
                    open={drawer?.type === 'load'}
                    batchId={drawer?.type === 'load' ? drawer.batchId : null}
                    stage={drawer?.type === 'load' ? drawer.stage : null}
                    direction={direction}
                    onClose={closeDrawer}
                    onOpenBagDetail={openBagDetail}
                    onOpenMoveBags={openMoveBags}
                />
            )}
            {/* 打开时才挂载：包牌抽屉关闭后不销毁浮层容器，常驻挂载时会被之后重新打开的批次详情盖住 */}
            {drawer?.type === 'bag' && <BagDetailDrawer visible bagNumber={drawer.bagNumber} onClose={closeDrawer} />}
            <NewBatchDrawer
                open={drawer?.type === 'newBatch'}
                onClose={closeDrawer}
                onCreated={(batches) => {
                    setPage(1);
                    setDrawer(batches.length ? { type: 'createBags', batches } : null);
                    if (outbound) refetchOutbound();
                }}
            />
            <CreateBagsDrawer
                open={drawer?.type === 'createBags'}
                batches={drawer?.type === 'createBags' ? drawer.batches : []}
                onClose={() => {
                    closeDrawer();
                    refreshListAndDetail();
                }}
                onOpenPrint={(jobs) => {
                    openPrintFromDrawer(jobs);
                    refreshListAndDetail();
                }}
            />
            <MoveInDrawer
                open={drawer?.type === 'moveIn'}
                source={drawer?.type === 'moveIn' ? drawer.source : null}
                warehouseId={Number(warehouseId)}
                onClose={() => {
                    closeDrawer();
                    refreshListAndDetail();
                }}
            />
            <MoveOutDrawer open={drawer?.type === 'moveOut'} warehouseId={Number(warehouseId)} onClose={closeDrawer} />

            <EndSortingModal
                open={endSortingTargets !== null}
                batches={endSortingTargets || []}
                onClose={() => setEndSortingTargets(null)}
                onEnded={() => {
                    setSelectedIds([]);
                    refreshListAndDetail();
                }}
            />
            <PrintQuantityModal
                open={printJobs !== null}
                jobs={printJobs || []}
                onClose={() => setPrintJobs(null)}
                onConfirm={(jobs) => {
                    setPrintJobs(null);
                    sendToPrinter(jobs);
                }}
            />
        </div>
    );
};

/**
 * 分拣批次（仅尾程业务仓）。数据全部为前端 mock，见 ./mock；接口待后端提供（todo batch mock）。
 */
const SortingBatchPage: React.FC = () => {
    const { t } = useTranslation();
    const { currentWarehouse } = useWarehouseContext();
    const permitted = hasResource([ResourceCode.menu.HUB_SORTING_BATCH]);

    if (!permitted) return <NoPermission />;
    if (!currentWarehouse) return null;
    // 仅尾程业务仓的 Hub 可用（同菜单口径；siteType 判断参照 wms/tt-exception-orders 页面守卫）
    if (currentWarehouse.siteType !== SiteType.HUB || currentWarehouse.businessType !== WarehouseBusinessType.LAST_MILE) {
        return <Result status="info" title={t('wms.sortingBatch.notLastMileHub')} />;
    }
    return (
        <SortingBatchProvider key={currentWarehouse.id} hubPrefix={resolveHubPrefix(currentWarehouse)}>
            <SortingBatchPageBody />
        </SortingBatchProvider>
    );
};

export default SortingBatchPage;
